import { ENTITY_TYPES } from '@sales-center/common'
import React from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { CreateAgreementTemplateDetails } from '@user/components/Agreements/AgreementFormPacketDialog/typings'
import MenuItem from '@mui/material/MenuItem'
import Box from '@mui/material/Box'
import { Select } from '@workwave-tidal/core/components'
import { useAgreementCreateActions } from '@user/components/Agreements/AgreementFormPacketDialog/hooks/useAgreementCreateActions'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { useTheme } from '@mui/material/styles'
import { getPositionMenuItemStyles } from '@user/utils/usePositionMenuItemStyles'
import Button from '@mui/material/Button'
import { AddServiceSection } from '@user/components/Agreements/AgreementFormPacketDialog/AddServiceSection'
import { useSelector } from 'react-redux'
import { selectAllServiceFrequencies } from '@user/store/serviceFrequency'
import CircularProgress from '@mui/material/CircularProgress'

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  openServicesDrawer: () => void
}

export function AgreementCreateFields(props: Props) {
  const { entityId, entityType, openServicesDrawer } = props
  const tidalThemeActive = useTidalThemeActive()
  const serviceFrequencies = useSelector(selectAllServiceFrequencies)
  const theme = useTheme()
  const positionMenuItemStyles = getPositionMenuItemStyles(theme)

  const {
    templates,
    handleTemplateChange,
    loading,
    services,
    agreement,
    onChangeService,
    toggleServices,
    agreementServices,
    getServiceLocationName,
    createAgreement,
  } = useAgreementCreateActions(entityId, entityType)

  const height = tidalThemeActive ? '65px' : '45px'

  return (
    <Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb="4px"
      >
        <Typography variant="h6">Select Services</Typography>
        <Stack direction="row">
          <Button
            onClick={openServicesDrawer}
            size="small"
            variant="text"
            sx={{
              height: 30,
              minWidth: 0,
              color: '#1565C0',
              textTransform: 'none',
            }}
            data-test-id="agreement-create-fields-view-manage-services-button"
          >
            View/Manage Services
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" mb="8px">
        <Button
          onClick={() => toggleServices('SELECT_ALL')}
          size="small"
          variant="text"
          sx={{
            height: 30,
            minWidth: 0,
            color: '#1565C0',
            textTransform: 'none',
          }}
          data-test-id="agreement-create-fields-select-all-button"
        >
          Select All
        </Button>
        <Button
          onClick={() => toggleServices('DESELECT_ALL')}
          size="small"
          variant="text"
          sx={{
            height: 30,
            minWidth: 0,
            color: '#1565C0',
            textTransform: 'none',
          }}
          data-test-id="agreement-create-fields-deselect-all-button"
        >
          Deselect All
        </Button>
      </Stack>

      <Stack gap={1}>
        {services.map((group, index) => {
          return (
            <Stack key={`services-group-${index}`}>
              <Typography variant="caption" mb={1}>
                Service Location:{' '}
                {getServiceLocationName(group.serviceLocationId)}
              </Typography>

              {group.services.map((service, serviceIndex) => {
                return (
                  <AddServiceSection
                    key={`services-item-${index}-${serviceIndex}`}
                    service={service}
                    agreementServices={agreementServices}
                    onChangeService={onChangeService}
                    serviceFrequencies={serviceFrequencies}
                  />
                )
              })}
            </Stack>
          )
        })}
      </Stack>

      <Typography variant="h6" mt="16px" mb="12px">
        Select Agreement Template
      </Typography>

      <Stack sx={{ width: '320px', mb: 2 }}>
        <Select<
          (CreateAgreementTemplateDetails & { isLabel?: boolean }) | undefined
        >
          getLabel={(option) => option?.templateFormName || ''}
          items={templates}
          autocompleteProps={{
            groupBy: (option: CreateAgreementTemplateDetails | undefined) => {
              if (option?.isTemplateAlreadyAdded === undefined) {
                return ''
              }
              if (option?.isTemplateAlreadyAdded) {
                return 'Existing Templates'
              }
              return 'New Templates'
            },
            renderGroup: (params) => (
              <li key={params.key}>
                {params.group && (
                  <MenuItem
                    disabled
                    sx={{
                      height: height,
                      minHeight: height,
                      ...positionMenuItemStyles.root,
                    }}
                  >
                    {params.group}
                  </MenuItem>
                )}
                <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                  {params.children}
                </Box>
              </li>
            ),
          }}
          onChange={(data) => handleTemplateChange(data)}
          value={agreement || null}
          testId={'selectAgreementTemplate'}
          data-test-id={'selectAgreementTemplate'}
          required
          size="small"
          label={'Agreement Template'}
          listMode="normal"
          optionMode={'custom'}
          disableClearOnBlur={false}
          renderOption={(props, template: CreateAgreementTemplateDetails) => (
            <MenuItem
              sx={{
                height: height,
                minHeight: height,
                position: 'relative',
              }}
              {...props}
              key={template.templateId}
              value={template.templateId}
            >
              <span style={{ width: '10px' }}></span>
              {template.templateFormName}
            </MenuItem>
          )}
        />
      </Stack>

      {!loading && (
        <Button
          size="medium"
          variant={tidalThemeActive ? 'contained' : 'outlined'}
          color={tidalThemeActive ? 'secondary' : 'primary'}
          sx={{
            height: 36,
            width: 'fit-content',
            minWidth: '126px',
            paddingX: 2,
            paddingY: '6px',
            mb: 4,
          }}
          disabled={!agreement || agreementServices.length === 0}
          onClick={createAgreement}
          data-test-id="agreement-create-fields-add-button"
        >
          Add
        </Button>
      )}

      {loading && (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}
    </Stack>
  )
}
